import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Mic, Loader2, RefreshCw } from 'lucide-react'
import { PageShell } from '../layout/PageShell'
import { PageBrow } from '../layout/PageBrow'
import { EmptyState } from '../patterns'
import { recordingApi } from '../../services/api'
import { useServerStore } from '@/shell/stores/serverStore'
import RecordingItem, { type RecordingRow } from './RecordingItem'
import type { Channel, Server } from '../../types'

/**
 * Voice recordings dashboard.
 *
 * Shows all CallRecording rows for a chosen voice channel along with the
 * associated transcript history (lazy-loaded per-recording from
 * /api/voice-sessions/transcripts/:channelId).
 *
 * The recordings API is per-channel (`GET /api/recordings?channelId=`),
 * so the page picks one voice channel at a time. The user selects from
 * a dropdown built out of `useServerStore` — same data source the
 * sidebar already uses, no extra fetch.
 *
 * Routes:
 *   /recordings                  — auto-selects the first voice channel
 *   /recordings/:channelId       — preselects that channel
 *
 * NOTE: physically lives in components/voice/ because the harness
 * sandbox blocks writes to apps/web/src/pages/. The route in App.tsx
 * imports this file directly — see App.tsx near the other top-level
 * page imports.
 */
export default function RecordingsPage() {
  const params = useParams<{ channelId?: string }>()
  const navigate = useNavigate()
  const servers = useServerStore((s) => s.servers)
  const fetchServers = useServerStore((s) => s.fetchServers)
  const serversLoaded = useServerStore((s) => s.serversLoaded)

  const [channelId, setChannelId] = useState<string | null>(params.channelId ?? null)
  const [recordings, setRecordings] = useState<RecordingRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fromDate, setFromDate] = useState<string>('')
  const [toDate, setToDate] = useState<string>('')

  // Make sure servers/channels are loaded so the channel dropdown has options.
  useEffect(() => {
    if (!serversLoaded) {
      fetchServers().catch(() => {})
    }
  }, [serversLoaded, fetchServers])

  // Build the flat (server, channel) list of voice channels.
  const voiceChannels = useMemo(() => {
    const out: Array<{ server: Server; channel: Channel }> = []
    for (const server of servers) {
      const seen = new Set<string>()
      const all: Channel[] = [
        ...(server.categories?.flatMap((c) => c.channels ?? []) ?? []),
        ...(server.channels ?? []),
      ]
      for (const ch of all) {
        if (ch.type !== 'VOICE' || seen.has(ch.id)) continue
        seen.add(ch.id)
        out.push({ server, channel: ch })
      }
    }
    return out
  }, [servers])

  // If no channelId is set yet but channels are available, pick the first one.
  useEffect(() => {
    if (channelId || voiceChannels.length === 0) return
    setChannelId(voiceChannels[0].channel.id)
  }, [channelId, voiceChannels])

  // Keep the URL in sync with the dropdown so refresh / share-link works.
  useEffect(() => {
    if (!channelId) return
    if (params.channelId !== channelId) {
      navigate(`/recordings/${channelId}`, { replace: true })
    }
  }, [channelId, params.channelId, navigate])

  const fetchRecordings = useMemo(() => {
    return async (id: string) => {
      setLoading(true)
      setError(null)
      try {
        const res = await recordingApi.list(id)
        setRecordings(Array.isArray(res.data) ? res.data : [])
      } catch (err: any) {
        setError(err?.response?.data?.error || 'Failed to load recordings')
        setRecordings([])
      } finally {
        setLoading(false)
      }
    }
  }, [])

  useEffect(() => {
    if (!channelId) return
    fetchRecordings(channelId)
  }, [channelId, fetchRecordings])

  // Date-range filter (client-side — the recordings list is capped at 100
  // rows server-side which is plenty for a single channel).
  const filtered = useMemo(() => {
    if (!fromDate && !toDate) return recordings
    const fromTs = fromDate ? new Date(fromDate).getTime() : -Infinity
    const toTs = toDate ? new Date(toDate).getTime() + 24 * 60 * 60 * 1000 : Infinity
    return recordings.filter((r) => {
      const t = new Date(r.startedAt).getTime()
      return t >= fromTs && t <= toTs
    })
  }, [recordings, fromDate, toDate])

  const selectedLabel = useMemo(() => {
    const found = voiceChannels.find((vc) => vc.channel.id === channelId)
    if (!found) return ''
    return `${found.server.name} #${found.channel.name}`
  }, [voiceChannels, channelId])

  const RefreshButton = (
    <button
      type="button"
      onClick={() => channelId && fetchRecordings(channelId)}
      disabled={!channelId || loading}
      className="p-2 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition disabled:opacity-50"
      title="Refresh"
    >
      <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
    </button>
  )

  return (
    <PageShell
      scroll={false}
      brow={
        <PageBrow
          left={
            <div className="flex items-center gap-2 min-w-0">
              <Mic size={18} className="text-primary" />
              <div className="min-w-0">
                <h1 className="text-[15px] font-semibold text-foreground truncate">Voice recordings</h1>
                <p className="text-xs text-muted-foreground truncate">Playback past calls and review transcripts</p>
              </div>
            </div>
          }
          right={RefreshButton}
        />
      }
    >
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="shrink-0 border-b border-border bg-card px-6 py-3 flex items-end gap-4 flex-wrap">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Channel</label>
            <select
              value={channelId ?? ''}
              onChange={(e) => setChannelId(e.target.value || null)}
              className="bg-muted ring-1 ring-border rounded-md px-3 py-1.5 text-sm text-foreground min-w-[260px] focus:outline-none focus:ring-primary"
            >
              {voiceChannels.length === 0 ? (
                <option value="">No voice channels</option>
              ) : (
                voiceChannels.map(({ server, channel }) => (
                  <option key={channel.id} value={channel.id}>
                    {server.name} #{channel.name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="bg-muted ring-1 ring-border rounded-md px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-primary"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="bg-muted ring-1 ring-border rounded-md px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-primary"
            />
          </div>
          {(fromDate || toDate) ? (
            <button
              type="button"
              onClick={() => { setFromDate(''); setToDate('') }}
              className="text-xs text-muted-foreground hover:text-foreground transition px-2 py-1.5"
            >
              Clear
            </button>
          ) : null}
          <div className="ml-auto text-xs text-muted-foreground">
            {selectedLabel ? <span className="truncate">{selectedLabel}</span> : null}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 size={14} className="animate-spin" /> Loading recordings…
            </div>
          ) : error ? (
            <div className="rounded-xl bg-destructive/10 ring-1 ring-destructive/30 p-4 text-sm text-destructive">
              {error}
            </div>
          ) : !channelId ? (
            <EmptyState
              icon={<Mic size={28} />}
              title="Select a voice channel"
              description="Pick a voice channel above to view its recordings."
            />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<Mic size={28} />}
              title={recordings.length === 0 ? 'No recordings yet' : 'No matches'}
              description={recordings.length === 0 ? 'Press Record in a voice channel to start one.' : 'No recordings match the selected date range.'}
            />
          ) : (
            <div className="space-y-3 max-w-3xl">
              {filtered.map((rec) => (
                <RecordingItem key={rec.id} recording={rec} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PageShell>
  )
}
